/**
 * Phase 408 wave 3 task 3.2 (ADR-089) — the console chat panel's two mutations.
 *
 * Both follow the console's established decision shape (usePostDecision.ts:14-27):
 * wait for the server, then INVALIDATE — no optimistic update. The reason is sharper here
 * than on a job decision: what the panel would have to optimistically invent is an agent's
 * answer or the outcome of an upstream write. The conversation store is the record of what
 * actually happened; a re-read after the 2xx is how the panel stays a view of that record
 * rather than a hopeful guess about it.
 *
 * Both invalidate the SAME two keys — the thread (["chat-messages", tenant, conversation])
 * and the conversation list (["chat-conversations", tenant]) — because both writes can
 * change both: a send can MINT a conversation, and a decision appends a receipt row.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchWithAuth } from "./fetcher.js";
import { CHAT_MESSAGES_PATH, chatDecisionsPath } from "./chatRoutes.js";
import type {
  ConsoleChatDecisionRequest,
  ConsoleChatDecisionResponse,
  ConsoleChatMessageRequest,
  ConsoleChatMessageResponse,
} from "./types.js";

/** What the composer hands the send mutation. */
export interface SendChatMessageVars {
  body: string;
  /** Omit to START a conversation — the 202 echoes the id the server minted. */
  conversationId?: string;
  /**
   * The inbound replay key. Supplied by the caller (not minted per attempt) so that a
   * human pressing Send again after a failed attempt re-sends THE SAME message rather
   * than asking the agent the same question twice: the server's
   * UNIQUE (tenant_id, conversation_id, client_message_id) turns the retry into a no-op
   * that returns `status: "replay"`. Minting inside this hook would defeat the guard
   * exactly when it is needed.
   */
  clientMessageId: string;
}

/**
 * `POST /internal/v0/chat/messages` — send one message in the named tenant.
 *
 * `tenant_id` is a LOCATOR on the wire, never an authorisation input: the engine enforces
 * it against the JWT principal's allowed set (OperatorTenantGuard, write:true), so this
 * value can only ever name a tenant the operator already holds.
 */
export function usePostChatMessage(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: SendChatMessageVars) => {
      const req: ConsoleChatMessageRequest = {
        tenant_id: tenantId,
        body: vars.body,
        client_message_id: vars.clientMessageId,
        ...(vars.conversationId ? { conversation_id: vars.conversationId } : {}),
      };
      return fetchWithAuth<ConsoleChatMessageResponse>(CHAT_MESSAGES_PATH, {
        method: "POST",
        body: JSON.stringify(req),
      });
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["chat-messages", tenantId, res.conversation_id] });
      qc.invalidateQueries({ queryKey: ["chat-conversations", tenantId] });
    },
  });
}

/** What a Confirm / Cancel / option tap hands the decision mutation. */
export interface ChatDecisionVars {
  /** The token carried on the card being answered, verbatim as the read returned it. */
  formToken: string;
  /** write_confirm: true executes the stored write, false cancels and writes nothing. */
  confirm?: boolean;
  /** clarification_selection: the chosen option's id (never its label). */
  selected?: string;
  /**
   * Card #3140 — write_confirm Confirm only: what the operator changed on the card's controlled rows
   * (ChatScreen `buildRowControls` — only the changed keys, `undefined` when nothing changed). It
   * reaches the wire ONLY beside `confirm: true` (`buildChatDecisionRequest`): a Cancel never
   * carries it, whatever a caller passes.
   */
  rowControls?: ConsoleChatDecisionRequest["row_controls"];
}

/**
 * Card #3140 — the engine's outcome for a Confirm whose row controls it refused (an over-long text, a
 * pick that is not one of the card's options): nothing was recorded and the card stays live. Mirrors
 * `ConsoleInboundDispatcher.OutcomeRowControlsRefused`. The response's `message` is the engine's
 * plain reason (it names the row's label), shown on the card in place of a re-read.
 */
export const OUTCOME_ROW_CONTROLS_REFUSED = "row_controls_refused";

/** Card #3140 — the reason to show on the card for a decision response, or null when it is not a
 *  row-controls refusal (every other outcome is read back from the thread instead). */
export function rowControlsRefusal(res: ConsoleChatDecisionResponse | undefined): string | null {
  if (!res || res.outcome !== OUTCOME_ROW_CONTROLS_REFUSED) return null;
  return typeof res.message === "string" && res.message.trim() !== ""
    ? res.message
    : "One of the card's rows could not be saved as given — nothing was recorded; change it and confirm again.";
}

/**
 * The decisions POST body for one tap. Pure, so the exact wire shape is unit-testable without a
 * network: the members are written in a fixed order and an absent option is ABSENT (not null), so an
 * untouched Confirm's body is byte-identical to the one the panel sent before card #3140.
 */
export function buildChatDecisionRequest(tenantId: string, vars: ChatDecisionVars): ConsoleChatDecisionRequest {
  return {
    tenant_id: tenantId,
    form_token: vars.formToken,
    ...(vars.confirm === undefined ? {} : { confirm: vars.confirm }),
    ...(vars.selected === undefined ? {} : { selected: vars.selected }),
    ...(vars.confirm === true && vars.rowControls !== undefined ? { row_controls: vars.rowControls } : {}),
  };
}

/**
 * `POST /internal/v0/chat/conversations/{id}/decisions` — answer one gated card.
 *
 * Every DECIDED outcome is a 200 with an `outcome` member of the server's closed
 * vocabulary ("executed", "already_executed", "declined", "expired", …). The panel does
 * not branch on it: on success it invalidates and re-reads, and the thread then carries
 * both the persisted receipt row and every card's refreshed `confirm_state`. That keeps
 * ONE renderer for "what happened", whether this session did it, a colleague did it on
 * another surface, or the token had already lapsed.
 *
 * The one exception (card #3140) is `row_controls_refused`: the engine recorded nothing — no
 * receipt, no consumed token — so there is nothing to re-read; the card shows the response's
 * reason (`rowControlsRefusal`, read by the card's own per-call `onSuccess`) and stays live with
 * the operator's edits intact.
 */
export function usePostChatDecision(tenantId: string, conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: ChatDecisionVars) =>
      fetchWithAuth<ConsoleChatDecisionResponse>(chatDecisionsPath(conversationId), {
        method: "POST",
        body: JSON.stringify(buildChatDecisionRequest(tenantId, vars)),
      }),
    onSuccess: (res) => {
      if (rowControlsRefusal(res) !== null) return;
      qc.invalidateQueries({ queryKey: ["chat-messages", tenantId, conversationId] });
      qc.invalidateQueries({ queryKey: ["chat-conversations", tenantId] });
    },
  });
}
